import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User, Role } from '@cms/models';

import type { TenantUserCreatePayload, TenantUserUpdatePayload } from '@/lib/api/tenant-user.api';
import { tenantUserApi } from '@/lib/api/tenant-user.api';
import { userApi } from '@/lib/api/user.api';
import { roleApi } from '@/lib/api/role.api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { usePermission } from '@/lib/rbac/usePermission';
import { CommonModal } from '@/components/common/CommonModal';

type TenantUserFormData = {
  tenantId: string;
  email: string;
  name: string;
  password: string;
  roleId: string;
  status: string;
};

const inputClass =
  'mt-1 block w-full h-9 px-3 py-1.5 text-sm bg-background border border-border rounded-md focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors';

export type TenantUserFormModalMode = 'create' | 'view' | 'edit';

export interface TenantUserFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly mode: TenantUserFormModalMode;
  /** null when mode is 'create' */
  readonly user: User | null;
  /** List of tenants for the Tenant dropdown in create mode */
  readonly tenants: Array<{ id?: string; name?: string | null; code?: string | null }>;
  /** Pre-fill tenant in create mode when opening from a page that has a tenant selected */
  readonly initialTenantId: string | null;
  readonly onSuccess?: () => void;
}

export function TenantUserFormModal({
  open,
  onClose,
  mode,
  user,
  tenants,
  initialTenantId,
  onSuccess,
}: TenantUserFormModalProps) {
  const queryClient = useQueryClient();
  const isCreate = mode === 'create';
  const isView = mode === 'view';
  const canReadMachineIds = usePermission('user_machine_id:read');
  const canWriteMachineIds = usePermission('user_machine_id:write');
  const canManageMachineIds = !isCreate && Boolean(user?.id) && canReadMachineIds;
  const [newMachineId, setNewMachineId] = useState('');
  const machineIdPattern = /^mid-[a-f0-9]{32}$/;
  const normalizedMachineId = newMachineId.trim().toLowerCase();
  const isMachineIdValid = machineIdPattern.test(normalizedMachineId);

  const { data: tenantRoles = [] } = useQuery<Role[]>({
    queryKey: ['roles', { isSystem: false, select: 'id,name' }],
    queryFn: () => roleApi.getAll({ isSystem: false, select: 'id,name' }),
  });
  const { data: machineIds = [] } = useQuery<string[]>({
    queryKey: ['tenant-user-machine-ids', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      return userApi.getMachineIds(user.id);
    },
    enabled: canManageMachineIds,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<TenantUserFormData>({
    defaultValues: {
      tenantId: '',
      email: '',
      name: '',
      password: '',
      roleId: '',
      status: 'active',
    },
  });

  useEffect(() => {
    if (user) {
      reset({
        tenantId: ('tenantId' in user ? user.tenantId : null) ?? '',
        email: user.email ?? '',
        name: user.name ?? '',
        password: '',
        roleId: user.roleId ?? '',
        status: (user.status ?? 'active') as string,
      });
    } else if (isCreate) {
      reset({
        tenantId: initialTenantId ?? '',
        email: '',
        name: '',
        password: '',
        roleId: '',
        status: 'active',
      });
    }
  }, [user, isCreate, initialTenantId, reset]);

  const createMutation = useMutation({
    mutationFn: (data: TenantUserFormData) => {
      if (!data.tenantId) return Promise.reject(new Error('Tenant is required'));
      const payload: TenantUserCreatePayload = {
        tenantId: data.tenantId,
        email: data.email,
        name: data.name || undefined,
        password: data.password || undefined,
        roleId: data.roleId || undefined,
      };
      return tenantUserApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      onSuccess?.();
      reset();
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: TenantUserFormData) => {
      if (!user?.id) return Promise.reject(new Error('No user'));
      const payload: TenantUserUpdatePayload = {
        name: data.name || undefined,
        status: (data.status as string) || undefined,
        roleId: data.roleId || undefined,
      };
      return tenantUserApi.update(user.id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      onSuccess?.();
      onClose();
    },
  });
  const addMachineMutation = useMutation({
    mutationFn: async (machineId: string) => {
      if (!user?.id) throw new Error('No user');
      return userApi.addMachineId(user.id, machineId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-user-machine-ids', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
      setNewMachineId('');
    },
  });
  const removeMachineMutation = useMutation({
    mutationFn: async (machineId: string) => {
      if (!user?.id) throw new Error('No user');
      return userApi.removeMachineId(user.id, machineId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-user-machine-ids', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['tenant-users'] });
    },
  });

  const onSubmit = async (data: TenantUserFormData) => {
    if (isView) return;
    try {
      if (isCreate) {
        if (!data.tenantId) {
          alert('Please select a tenant.');
          return;
        }
        await createMutation.mutateAsync(data);
      } else {
        await updateMutation.mutateAsync(data);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(
        err.response?.data?.message ??
          (isCreate ? 'Failed to create user' : 'Failed to update user')
      );
    }
  };

  const selectedTenantId = watch('tenantId');
  const title =
    mode === 'create'
      ? 'Create Tenant User'
      : mode === 'view'
        ? 'Tenant user details'
        : 'Edit tenant user';

  const readOnly = isView;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      titleId="tenant-user-form-modal-title"
      panelClassName="max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4"
        noValidate
      >
          {isCreate && (
            <div>
              <label
                htmlFor="tenant-user-form-tenantId"
                className="block text-sm font-medium text-foreground"
              >
                Tenant *
              </label>
              <select
                id="tenant-user-form-tenantId"
                {...register('tenantId', { required: isCreate ? 'Tenant is required' : false })}
                className={cn(inputClass, errors.tenantId && 'border-destructive')}
              >
                <option value="">Select tenant</option>
                {tenants.filter((t): t is { id: string } & typeof t => Boolean(t.id)).map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name ?? t.code ?? t.id}
                  </option>
                ))}
              </select>
              {errors.tenantId && (
                <p className="mt-1 text-sm text-destructive">
                  {errors.tenantId.message}
                </p>
              )}
            </div>
          )}

          {!isCreate && user && 'tenantId' in user && user.tenantId && (
            <div>
              <span className="block text-sm font-medium text-muted-foreground mb-1">
                Tenant
              </span>
              <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm">
                {tenants.find(t => t.id === user.tenantId)?.name ?? tenants.find(t => t.id === user.tenantId)?.code ?? user.tenantId}
              </span>
            </div>
          )}

          <div>
            <label
              htmlFor="tenant-user-form-email"
              className="block text-sm font-medium text-foreground"
            >
              Email *
            </label>
            <input
              id="tenant-user-form-email"
              type="email"
              {...register('email', {
                required: isCreate ? 'Email is required' : false,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              disabled={readOnly || !isCreate}
              readOnly={!isCreate}
              className={cn(
                inputClass,
                (readOnly || !isCreate) && 'cursor-default bg-muted/50'
              )}
            />
            {!isCreate && (
              <p className="mt-1 text-xs text-muted-foreground">
                Email cannot be changed
              </p>
            )}
            {errors.email && (
              <p className="mt-1 text-sm text-destructive">
                {errors.email.message}
              </p>
            )}
          </div>

          {isCreate && (
            <div>
              <label
                htmlFor="tenant-user-form-password"
                className="block text-sm font-medium text-foreground"
              >
                Password (optional, min 8 characters)
              </label>
              <input
                id="tenant-user-form-password"
                type="password"
                {...register('password', {
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                })}
                className={inputClass}
              />
              {errors.password && (
                <p className="mt-1 text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="tenant-user-form-name"
                className="block text-sm font-medium text-foreground"
              >
                Name
              </label>
              <input
                id="tenant-user-form-name"
                type="text"
                {...register('name')}
                disabled={readOnly}
                className={cn(
                  inputClass,
                  readOnly && 'cursor-default bg-muted/50'
                )}
              />
            </div>
            {readOnly ? (
              <div>
                <span className="block text-sm font-medium text-muted-foreground mb-1">
                  Status
                </span>
                <span className="inline-flex items-center rounded-md border border-border px-2.5 py-1 text-sm capitalize">
                  {watch('status') || '—'}
                </span>
              </div>
            ) : (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Toggle
                    id="tenant-user-form-status"
                    label="Status"
                    checked={field.value === 'active'}
                    onCheckedChange={checked =>
                      field.onChange(checked ? 'active' : 'inactive')
                    }
                  />
                )}
              />
            )}
          </div>

          <div>
            <label
              htmlFor="tenant-user-form-roleId"
              className="block text-sm font-medium text-foreground"
            >
              Role
            </label>
            <select
              id="tenant-user-form-roleId"
              {...register('roleId')}
              disabled={readOnly}
              className={cn(
                inputClass,
                readOnly && 'cursor-default bg-muted/50'
              )}
            >
              <option value="">— No role —</option>
              {tenantRoles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          {canManageMachineIds && (
            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Machine IDs</h3>
                <span className="text-xs text-muted-foreground">{machineIds.length} registered</span>
              </div>

              <div className="space-y-2">
                {machineIds.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No machine IDs registered.</p>
                ) : (
                  machineIds.map(machineId => (
                    <div
                      key={machineId}
                      className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5"
                    >
                      <code className="flex-1 truncate text-xs">{machineId}</code>
                      {!readOnly && canWriteMachineIds && (
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive"
                          title="Remove machine ID"
                          aria-label={`Remove ${machineId}`}
                          onClick={() => removeMachineMutation.mutate(machineId)}
                          disabled={removeMachineMutation.isPending}
                        >
                          X
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>

              {!readOnly && canWriteMachineIds && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMachineId}
                    onChange={e => setNewMachineId(e.target.value)}
                    placeholder="mid-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    className={inputClass}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addMachineMutation.mutate(normalizedMachineId)}
                    disabled={!isMachineIdValid || addMachineMutation.isPending}
                  >
                    Add
                  </Button>
                </div>
              )}
              {!readOnly && canWriteMachineIds && newMachineId.trim() && !isMachineIdValid && (
                <p className="text-xs text-destructive">Machine ID must match `mid-[32 hex chars]`.</p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            {readOnly ? (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    isCreate
                      ? createMutation.isPending || !selectedTenantId
                      : updateMutation.isPending
                  }
                >
                  {isCreate
                    ? createMutation.isPending
                      ? 'Creating…'
                      : 'Create'
                    : updateMutation.isPending
                      ? 'Saving…'
                      : 'Save'}
                </Button>
              </>
            )}
          </div>
      </form>
    </CommonModal>
  );
}
